import { QueryResult, OperationVariables } from "@apollo/client";
import Button from "@/components/bricks/button/Button";
import { useNavigate } from "react-router-dom";
import { QUERY_PAGE_SIZE } from "../../api/util";
import {
  PagedFirmaContext,
  PagedKursusContext,
  PagedLinjeContext,
  PagedPersonContext,
} from "../../interfaces/pagedContext";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import {
  // Firma,
  Fmulinje,
  Kursushold,
  PersonDto,
  FirmaDto,
} from "../../interfaces/schema";
import { PagedLinjeResponse } from "../../interfaces/linje";
import { IPagedKursusResponse } from "../../interfaces/kursus";
import { IPagedFirmaResponse } from "../../interfaces/firma";
import { IPagedPersonsResponse } from "../../interfaces/personer";

export function IDPagination(props: {
  pagedContext:
    | PagedPersonContext
    | PagedFirmaContext
    | PagedKursusContext
    | PagedLinjeContext;
  currentSelection: PersonDto | FirmaDto | Kursushold | Fmulinje;
}) {
  const navigate = useNavigate();

  const { pagedContext, currentSelection } = props;
  const { pagedResponse, query, page, nextPage, prevPage } = pagedContext;

  if (!pagedResponse || !query) {
    return <div style={{ width: 130 }}></div>;
  }

  let indexInCurrentPage: number;
  let indexInPrevPage: number = 0;
  let indexInNextPage: number = 0;
  let totalCount: number;

  let onNextClick: Function;
  let onPreviousClick: Function;

  switch (pagedContext.type) {
    case "person":
      const { personer } = pagedResponse as IPagedPersonsResponse;
      totalCount = personer.totalCount;
      indexInCurrentPage = personer.edges.findIndex(
        (e) => e.node.id === currentSelection.id,
      );

      if (prevPage) {
        indexInPrevPage =
          (
            prevPage as QueryResult<IPagedPersonsResponse, OperationVariables>
          ).data?.personer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      if (nextPage) {
        indexInNextPage =
          (
            nextPage as QueryResult<IPagedPersonsResponse, OperationVariables>
          ).data?.personer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      onNextClick = () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = personer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const next = pagedResponse?.personer.edges[i + 1];
          if (next) {
            navigate(`/person/${next.node.id}`);
          } else {
            if (pagedContext.nextPage?.data?.personer.edges.length) {
              navigate(
                `/person/${pagedContext.nextPage?.data?.personer.edges[0].node.id}`,
              );
              setQuery({
                ...query,
                page: page + 1,
                before: undefined,
                after: personer.pageInfo.endCursor,
              });
            }
          }
        }
      };

      onPreviousClick = async () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = personer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const prev = pagedResponse?.personer.edges[i - 1];
          if (prev) {
            navigate(`/person/${prev.node.id}`);
          } else {
            if (pagedContext.prevPage?.data?.personer.edges.length) {
              const prevId =
                pagedContext.prevPage?.data?.personer.edges[
                  pagedContext.prevPage?.data?.personer.edges.length - 1
                ].node.id;
              navigate(`/person/${prevId}`);
              setQuery({
                ...query,
                page: page - 1,
                before: personer.pageInfo.startCursor,
                after: undefined,
              });
            }
          }
        }
      };
      break;

    case "firma":
      const { firmaer } = pagedResponse as IPagedFirmaResponse;

      totalCount = firmaer.totalCount;
      indexInCurrentPage = firmaer.edges.findIndex(
        (e) => e.node.id === currentSelection.id,
      );

      if (prevPage) {
        indexInPrevPage =
          (
            prevPage as QueryResult<IPagedFirmaResponse, OperationVariables>
          ).data?.firmaer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      if (nextPage) {
        indexInNextPage =
          (
            nextPage as QueryResult<IPagedFirmaResponse, OperationVariables>
          ).data?.firmaer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      onNextClick = () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = firmaer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const next = pagedResponse?.firmaer.edges[i + 1];
          if (next) {
            navigate(`/firma/${next.node.id}`);
          } else {
            if (pagedContext.nextPage?.data?.firmaer.edges.length) {
              navigate(
                `/firma/${pagedContext.nextPage?.data?.firmaer.edges[0].node.id}`,
              );
              const { firmaer } = pagedResponse;
              const { pageInfo } = firmaer;
              setQuery({
                ...query,
                page: page + 1,
                before: undefined,
                after: pageInfo.endCursor,
              });
            }
          }
        }
      };

      onPreviousClick = async () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = firmaer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const prev = pagedResponse?.firmaer.edges[i - 1];
          if (prev) {
            navigate(`/firma/${prev.node.id}`);
          } else {
            if (pagedContext.prevPage?.data?.firmaer.edges.length) {
              const prevId =
                pagedContext.prevPage?.data?.firmaer.edges[
                  pagedContext.prevPage?.data?.firmaer.edges.length - 1
                ].node.id;
              const { firmaer } = pagedResponse;
              const { pageInfo } = firmaer;
              navigate(`/firma/${prevId}`);
              setQuery({
                ...query,
                page: page - 1,
                before: pageInfo.startCursor,
                after: undefined,
              });
            }
          }
        }
      };
      break;

    case "kursus":
      const { kursusholds } = pagedResponse as IPagedKursusResponse;

      totalCount = kursusholds.totalCount;
      indexInCurrentPage = kursusholds.edges.findIndex(
        (e) => e.node.id === currentSelection.id,
      );

      if (prevPage) {
        indexInPrevPage =
          (
            prevPage as QueryResult<IPagedKursusResponse, OperationVariables>
          ).data?.kursusholds.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      if (nextPage) {
        indexInNextPage =
          (
            nextPage as QueryResult<IPagedKursusResponse, OperationVariables>
          ).data?.kursusholds.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      onNextClick = () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = kursusholds.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const next = pagedResponse?.kursusholds.edges[i + 1];
          if (next) {
            navigate(`/kursus/${next.node.id}`);
          } else {
            if (pagedContext.nextPage?.data?.kursusholds.edges.length) {
              navigate(
                `/kursus/${pagedContext.nextPage?.data?.kursusholds.edges[0].node.id}`,
              );
              const { kursusholds } = pagedResponse;
              const { pageInfo } = kursusholds;
              setQuery({
                ...query,
                page: page + 1,
                before: undefined,
                after: pageInfo.endCursor,
              });
            }
          }
        }
      };

      onPreviousClick = async () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = kursusholds.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const prev = pagedResponse?.kursusholds.edges[i - 1];
          if (prev) {
            navigate(`/kursus/${prev.node.id}`);
          } else {
            if (pagedContext.prevPage?.data?.kursusholds.edges.length) {
              const prevId =
                pagedContext.prevPage?.data?.kursusholds.edges[
                  pagedContext.prevPage?.data?.kursusholds.edges.length - 1
                ].node.id;
              const { kursusholds } = pagedResponse;
              const { pageInfo } = kursusholds;
              navigate(`/kursus/${prevId}`);
              setQuery({
                ...query,
                page: page - 1,
                before: pageInfo.startCursor,
                after: undefined,
              });
            }
          }
        }
      };
      break;

    case "linje":
      const { fmulinjer } = pagedResponse as PagedLinjeResponse;

      totalCount = fmulinjer.totalCount;
      indexInCurrentPage = fmulinjer.edges.findIndex(
        (e) => e.node.id === currentSelection.id,
      );

      if (prevPage) {
        indexInPrevPage =
          (
            prevPage as QueryResult<PagedLinjeResponse, OperationVariables>
          ).data?.fmulinjer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      if (nextPage) {
        indexInNextPage =
          (
            nextPage as QueryResult<PagedLinjeResponse, OperationVariables>
          ).data?.fmulinjer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          ) ?? -1;
      }

      onNextClick = () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = fmulinjer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const next = pagedResponse?.fmulinjer.edges[i + 1];
          if (next) {
            navigate(`/linje/${next.node.id}`);
          } else {
            if (pagedContext.nextPage?.data?.fmulinjer.edges.length) {
              navigate(
                `/linje/${pagedContext.nextPage?.data?.fmulinjer.edges[0].node.id}`,
              );
              const { fmulinjer } = pagedResponse;
              const { pageInfo } = fmulinjer;
              setQuery({
                ...query,
                page: page + 1,
                before: undefined,
                after: pageInfo.endCursor,
              });
            }
          }
        }
      };

      onPreviousClick = async () => {
        const { query, setQuery, page, pagedResponse } = pagedContext;

        if (pagedResponse) {
          const i = fmulinjer.edges.findIndex(
            (e) => e.node.id === currentSelection.id,
          );
          const prev = pagedResponse?.fmulinjer.edges[i - 1];
          if (prev) {
            navigate(`/linje/${prev.node.id}`);
          } else {
            if (pagedContext.prevPage?.data?.fmulinjer.edges.length) {
              const prevId =
                pagedContext.prevPage?.data?.fmulinjer.edges[
                  pagedContext.prevPage?.data?.fmulinjer.edges.length - 1
                ].node.id;
              const { fmulinjer } = pagedResponse;
              const { pageInfo } = fmulinjer;
              navigate(`/linje/${prevId}`);
              setQuery({
                ...query,
                page: page - 1,
                before: pageInfo.startCursor,
                after: undefined,
              });
            }
          }
        }
      };
      break;

    default:
      indexInCurrentPage = 0;
      indexInPrevPage = 0;
      indexInNextPage = 0;
      totalCount = 0;
      onPreviousClick = () => {};
      onNextClick = () => {};
      break;
  }

  const i = Math.max(indexInCurrentPage, indexInNextPage, indexInPrevPage);

  if (i < 0 || !page) {
    return (
      <div className="c-pagination">
        <div className="c-pagination__text">
          <span className="text-muted">1 af 1</span>
        </div>
        <div className="c-pagination__links">
          <Button
            color="secondary"
            className="text-muted"
            icon
            transparent
            disabled
            aria-label="Forrige"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            color="secondary"
            className="text-muted"
            icon
            transparent
            disabled
            aria-label="Næste"
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
    );
  }
  const numberBefore = (page - 1) * QUERY_PAGE_SIZE;
  const index = numberBefore + (i + 1);

  return (
    <div className="c-pagination">
      <div className="c-pagination__text">
        {index} <span className="text-muted">af {totalCount}</span>
      </div>
      <div className="c-pagination__links">
        <Button
          color="secondary"
          icon
          transparent
          disabled={index === 1}
          aria-label="Forrige"
          onClick={onPreviousClick}
        >
          <ChevronLeftIcon />
        </Button>
        <Button
          color="secondary"
          icon
          transparent
          disabled={index === totalCount}
          aria-label="Næste"
          onClick={onNextClick}
        >
          <ChevronRightIcon />
        </Button>
      </div>
    </div>
  );
}
